import React, {useRef} from 'react';

import mergeRefs from 'merge-refs';

import {Group} from '@pb/kit-component-group';
import {Icons, Sizes} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {Input} from '@pb/kit-component-input-v2';
import {useControlledState} from '@pb/kit-hooks-use-controlled-state';

import {AutosuggestList} from './autosuggest-list.component';
import {useAutosuggest} from './use-autosuggest';

import type {UseAutosuggestProps} from './use-autosuggest';
import type {InputProps} from '@pb/kit-component-input-v2';
import type {ListboxProps} from '@pb/kit-component-listbox';
import type {ComponentPropsWithoutRef, ReactNode} from 'react';


export interface AutosuggestProps extends Omit<InputProps, 'type' | 'onToggle'> {
    /**
     * When provided, shows an × clear button next to the search icon whenever
     * the input has a non-empty `value`. Clicking the button calls this callback
     * and refocuses the input.
     */
    onClear?: () => void,
    /**
     * Extra props forwarded to the inner `<Listbox>` floating panel (e.g.
     * `className`, custom `bodyComponent`, or ARIA overrides).
     */
    listboxProps?: Partial<ListboxProps>,
    /**
     * Extra props forwarded to the Floating UI list-navigation plugin (e.g.
     * `focusItemOnOpen`, `loop`).
     */
    listNavigationProps?: UseAutosuggestProps['listNavigationProps'],
    /**
     * Content of the suggestion listbox: `<AutosuggestItem>`, `<AutosuggestList>`,
     * `<AutosuggestNoResults>`, `<AutosuggestListCaption>`, etc.
     */
    children?: ReactNode,
    /**
     * Called when the user selects a suggestion. Receives the `value` of the
     * selected `<AutosuggestItem>` and the selection trigger
     * (`"MOUSE_DOWN"` or `"ENTER"`).
     */
    onItemSelect?: UseAutosuggestProps['onItemSelect'],
    /**
     * When `true` (default), closing the listbox after an item is selected.
     * Set to `false` to keep the list open (useful for multi-select patterns).
     */
    hideOnItemClick?: UseAutosuggestProps['hideOnItemClick'],
    /**
     * When set, the listbox width extends beyond the anchor in the given
     * direction instead of matching the input width exactly.
     * `"left"` aligns to the right edge; `"right"` aligns to the left edge.
     */
    direction?: UseAutosuggestProps['direction'],
    /**
     * Number of items visible before the listbox starts scrolling. Default `10`.
     */
    itemsForScroll?: UseAutosuggestProps['itemsForScroll'],
    /**
     * Called whenever the listbox opens or closes. Useful for fetching
     * suggestions lazily on first open.
     */
    onToggle?: UseAutosuggestProps['onToggle'],
    /**
     * Floating UI positioning strategy passed to the listbox (`"absolute"` or `"fixed"`).
     */
    type?: UseAutosuggestProps['type'],
    /**
     * Ordered list of Floating UI placement strings the listbox may use.
     * The first placement that fits the viewport is selected automatically.
     */
    allowedPlacements?: UseAutosuggestProps['allowedPlacements'],
    /**
     * Controls listbox visibility externally. Pair with `setShown` for a
     * fully controlled open/close state.
     */
    shown?: UseAutosuggestProps['shown'],
    /**
     * Setter for the controlled `shown` state. Called with the new boolean
     * and an optional reason string whenever the listbox should open or close.
     */
    setShown?: UseAutosuggestProps['setShown'],
    /**
     * When `true` (default when `direction` is not set), the listbox width
     * matches the anchor input width exactly.
     */
    equalWidth?: UseAutosuggestProps['equalWidth'],
    /**
     * Element rendered in the right adornment slot of the input. Defaults to
     * a search icon. Pass `null` to disable the default icon entirely.
     */
    endAdornment?: ReactNode,
    /**
     * Props forwarded to the `<Group>` container that wraps the clear button
     * and `endAdornment` together.
     */
    endAdornmentGroupProps?: ComponentPropsWithoutRef<typeof Group>,
    /**
     * When `true` (default), the listbox closes automatically when the input
     * value is cleared. Set to `false` to keep the list open after clearing.
     */
    isClosedOnClear?: boolean,
}

/**
 * The `Autosuggest` component is an input field that provides selectable suggestions on user input. It is used to improve
 * the user experience by allowing quick search and select options from large collections.
 *
 * **Features:**
 *
 * * Renders Listbox with suggestions next to the input field.
 * * Supports keyboard navigation.
 *
 * **Accessibility:**
 *
 * Implements [WAI-ARIA design pattern for combobox](https://www.w3.org/WAI/ARIA/apg/patterns/combobox/). Supports keyboard navigation, all the ARIA attributes.
 *
 * **Migration to the V2 version:**
 *
 * * Props are inherited from InputV2 and Listbox props, so onBlur etc are replaced with native props, inputProps is removed, listboxProps is added.
 * * Cause props are inherited from Listbox props, scrollableConfig, type and the rest props for list are replaced with listboxProps.
 * * Highlight feature is removed. Use caption as node.
 * * Query, onChangeQuery and rest of related props are removed, including debounce on change query.
 * * Suggestions - Item is ListBoxItemProps with data object. Use data property to pass item props from suggestions to the onItemSelect callback. RenderSuggestions is removed, renderer is removed.
 * * As far as props are inherited from Input - handleOnAfterIconAction, disableInputIconAfter, onIconAfterClick, inputIcon are removed, use `endAornment` instead for example.
 * * 'clearOnChange' is removed, clear the value in the onItemSelect callback externally.
 * * 'clearable' is removed, use `onClear` instead.
 * * 'renderCustomList' is removed, use children instead.
 * * 'listCaption' is removed, add ListCaption component in children instead.
 * * 'showSuggestionsOnFocus' is removed, use 'onClick', 'shown', 'setShown' props.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-autosuggest--documentation}
 * */
export function Autosuggest(props: AutosuggestProps) {
    const {
        onItemSelect,
        onClear,
        hideOnItemClick,
        direction,
        itemsForScroll,
        onToggle,
        placeholder,
        name,
        type,
        allowedPlacements,
        listboxProps,
        listNavigationProps,
        value,
        children,
        shown: externalShown,
        setShown: externalSetShown,
        equalWidth,
        isClosedOnClear,
        endAdornmentGroupProps,
        ref,
        ...inputProps
    } = props;

    const nativeInputRef = useRef<HTMLInputElement | null>(null);

    const [shown, setShown] = useControlledState(externalShown, externalSetShown, false);

    const {
        getAnchorProps,
        getAutosuggestListProps,
        anchorRef,
    } = useAutosuggest({
        equalWidth,
        shown,
        setShown,
        onItemSelect,
        hideOnItemClick,
        itemsForScroll,
        isClosedOnClear,
        direction,
        onToggle,
        type,
        allowedPlacements,
        listNavigationProps,
    });

    const defaultInputIcon = (
        <BasicIconButton
            className="pointer-events-none"
            key="search"
            tabIndex={-1}
            aria-hidden={true}
            disabled={inputProps?.disabled}
            iconProps={{
                size: Sizes.Size_14,
                type: Icons.SEARCH,
            }}
        />
    );

    const renderEndAdornment = () => {
        const {endAdornment = defaultInputIcon} = inputProps;

        return (
            <Group
                gap="s"
                className={endAdornment === defaultInputIcon ? 'pointer-events-none' : undefined}
                {...endAdornmentGroupProps}>
                {onClear && value ? (
                    <BasicIconButton
                        className="pointer-events-auto"
                        iconProps={{
                            size: Sizes.Size_19,
                            type: Icons.CLOSE,
                        }}
                        aria-label="Clear"
                        key="clear"
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                onClear();
                                nativeInputRef.current?.focus();
                            }
                        }}
                        onClick={() => {
                            onClear();
                            nativeInputRef.current?.focus();
                        }}
                    />
                ) : null}
                {endAdornment}
            </Group>
        );
    };

    return (
        <>
            <Input
                placeholder={placeholder}
                name={name}
                autoCapitalize="none"
                spellCheck="false"
                aria-autocomplete="list"
                maxLength={512}
                {...getAnchorProps(inputProps)}
                endAdornment={renderEndAdornment()}
                ref={mergeRefs(nativeInputRef, anchorRef, ref) as React.Ref<HTMLInputElement>}
                value={value}
            />
            <AutosuggestList
                aria-label={inputProps['aria-label']}
                aria-labelledby={inputProps['aria-labelledby']}
                {...getAutosuggestListProps(listboxProps)}>
                {children}
            </AutosuggestList>
        </>
    );
}
